import { Component, OnInit } from '@angular/core';
import { IndicationDefinition, IndicationGroup } from '@app/models/indication';
import { ControlTypeEnum, SelectionsModel } from '@app/models/selections-model';
import { SharedService } from '@app/services/shared.service';
import { vennHealthConstants } from '../vennHealthConstants';

@Component({
  selector: 'app-filter-selections',
  templateUrl: './filter-selections.component.html',
  styleUrls: ['./filter-selections.component.css']
})
export class FilterSelectionsComponent implements OnInit {

  allIndications: IndicationGroup[] = [];
  selectedDataSets: any = [];

  constructor(private sharedService: SharedService) { }

  ngOnInit() {
    this.sharedService.allIndications.subscribe(data => {
      if (data) {
        this.allIndications = data;
        this.runFinalValidationCheck();
      }
    });

    this.sharedService.selectedDataSets.subscribe(data => {
      if (data) {
        this.selectedDataSets = data;
        this.runFinalValidationCheck();
      }
    });

    this.sharedService.forceRunValidationCheck.subscribe(data => {
      if (data && data === true) {
        this.runFinalValidationCheck();
      }
    })
  }

  runFinalValidationCheck(): boolean {
    let invalidSelections = [];

    // validate datasets
    if (this.selectedDataSets.length === 0) {
      invalidSelections.push({
        indicationName: 'datasets', 
        indicationGroupDisplayName: 'Data Sets',
        errorMessage: 'Please select one or more data set to query against.'
      });
    }

    // validate indications
    this.allIndications.forEach(group => {
      group.indications.forEach(indication => {
        // only selected indications
        if (indication.selected) {
          // only non-diagnosed only controls
          if (!indication.diagnosedOnly) {
            // no selections at all
            if (!indication.selections || indication.selections.length === 0) {

              invalidSelections.push({
                indicationName: indication.name, 
                indicationGroupDisplayName: group.displayName,
                indicationGroupName: group.name,
                errorMessage: `${indication.displayName}: Valid selections are required to proceed.`
              });
            } else {
              if (indication.name === 'dyslipidemia') {
                const invalidDyslipidemiaSelection = this.getInvalidDyslipidemiaSelections(indication, group);
                if (invalidDyslipidemiaSelection && invalidDyslipidemiaSelection.length > 0) {
                  invalidDyslipidemiaSelection.forEach(i => invalidSelections.push(i));
                }
              } else {
                // iterate through each selected control
                indication.selections.forEach(selection => {
                  if ((selection.controlType === ControlTypeEnum.TEXT || selection.controlType === ControlTypeEnum.RADIO || 
                    selection.controlType === ControlTypeEnum.DROPDOWN) && !selection.selectionValue) {
                      selection['indicationGroupDisplayName'] = group.displayName;
                      selection['indicationGroupName'] = group.name;
                      selection['errorMessage'] = `${indication.displayName} - ${selection.controlDisplayName}: Please input a valid value.`;
                    // string or number value is empty
                    invalidSelections.push(selection);
                  }
                  if ((selection.controlType === ControlTypeEnum.CHECKBOX || selection.controlType === ControlTypeEnum.RANGE_SLIDER) && 
                    // no value or empty array []
                    (!selection.selectionValue || (selection.selectionValue && selection.selectionValue.length === 0))) {
                      selection['indicationGroupDisplayName'] = group.displayName;
                      selection['indicationGroupName'] = group.name;
                      selection['errorMessage'] = `${indication.displayName}: Please make a valid selection.`;
                      invalidSelections.push(selection);
                  }
                });
              }
            }
          }
        }
      });
    });
    this.sharedService.setValidationErrors(invalidSelections);
    const valid = invalidSelections.length == 0;
    return valid;
  }

  getInvalidDyslipidemiaSelections(indication: IndicationDefinition, group: IndicationGroup): any[] {
    let validationErrors = [];
    if (!indication.selections.some(i => i.controlType === ControlTypeEnum.CHECKBOX && i.selectionValue === true)) {
      return [
        {
          indicationName: indication.name, 
          indicationGroupDisplayName: group.displayName,
          indicationGroupName: group.name,
          errorMessage: `${indication.displayName}: At least 1 criteria selection is required.`
        }
      ];
    }

    // prefixes of all selected checkboxes to see what textboxes we need to validate below (example: HDL_, LDL_, TC_, TT_)
    const enabledSelectionCheckboxPrefixes = indication.selections.filter(i => i.controlType === ControlTypeEnum.CHECKBOX && i.selectionValue === true).map(i => i.controlName.split('_')[0]);
    indication.selections.forEach(selection => {
      const selectedControlPrefix = selection.controlName.split('_')[0];
      if (selection.controlType === ControlTypeEnum.TEXT && enabledSelectionCheckboxPrefixes.includes(selectedControlPrefix)) {
        if (!selection.selectionValue) {
          const controlFullDisplayName = indication.selections.find(i => i.controlType === ControlTypeEnum.CHECKBOX && i.controlName.split('_')[0] === selectedControlPrefix).controlDisplayName;
          validationErrors.push({
            indicationName: indication.name, 
            indicationGroupDisplayName: group.displayName,
            indicationGroupName: group.name,
            errorMessage: `${indication.displayName} - ${controlFullDisplayName} ${selection.controlDisplayName}: Please input a valid value.`
          });
        }
      }
    })
    return validationErrors;
  }

  groupHasIndicationSelections(indicationGroup: IndicationGroup): boolean {
    return indicationGroup.indications.some(i => i.selected);
  }

  indicationGroupSelectionsCount(indicationGroup: IndicationGroup): string {
    return this.groupHasIndicationSelections(indicationGroup) ? `(${indicationGroup.indications.filter(i => i.selected).length})` : '';
  }

  displayInputSelection(selection: SelectionsModel, indicationSelections: SelectionsModel[]): string {
    const indicationsWithCriteriaToggleAndNoCustomFilterFunction = ['hypertension', 'ckd', 'diabetes', 'bodyWeight'];
    const isIndicationWithCriteriaToggle = indicationsWithCriteriaToggleAndNoCustomFilterFunction.includes(selection.indicationName);
    const hasDiagnosedOrTreatedOnlySelected = indicationSelections.some(i => vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(i.selectionValue.toString()));
    let display = '';
    if (hasDiagnosedOrTreatedOnlySelected && isIndicationWithCriteriaToggle) {
      // covers diabetes with populations and diagnosis controls
      if (indicationSelections.some(i => i.controlName === 'populations')) {
        if (['diagnosis', 'populations'].includes(selection.controlName)) {
          display = `${selection.controlDisplayName}: ${selection.selectionDisplayName}`;
        }
      }
      else if (selection.controlName === 'diagnosis') {
        display = `${selection.controlDisplayName}: ${selection.selectionDisplayName}`;
      }
    } else {
      switch (selection.controlType) {
        case ControlTypeEnum.TEXT: {
          display = `${selection.controlDisplayName}: ${selection.selectionValue}`;
          break;
        }
        case ControlTypeEnum.CHECKBOX: {
          display = `${selection.controlDisplayName}: ${selection.selectionValue.join(" & ")}`;
          break;
        }
        case ControlTypeEnum.RADIO: {
          display = `${selection.controlDisplayName}: ${selection.selectionDisplayName}`;
          break;
        }
        case ControlTypeEnum.DROPDOWN: {
          display = `${selection.controlDisplayName}: ${selection.selectionDisplayName}`;
          break;
        }
        case ControlTypeEnum.RANGE_SLIDER: {
          display = selection.selectionDisplayName;
          break;
        }
        case ControlTypeEnum.CUSTOM: {
          if (selection.controlName === 'condition') {
              display = selection.selectionValue;
              break;
          }
        }
        case ControlTypeEnum.IGNORE_IN_UI: {
          display = '';
          break;
        }
        default: {
          display = `${selection.selectionValue}`;
          break;
        }
      }
      display = this.appendSuffix(display, selection);
    }
    return display;
  }

  appendSuffix(display: string, selection: SelectionsModel): string {
    if (selection.indicationName === vennHealthConstants.customControlIndications.tenYearRiskOfCHDFraminghamScore) {
      display += '%';
    }
    return display;
  }

  displayDislipidemia(indicationSelections: SelectionsModel[]): void {
    // get the dom element to mod
    let html = document.getElementById('dyslipidemiaFilters');

    // clear previous html
    html.innerHTML = '';

    // create a new element to add things to
    let div = document.createElement('div');

    let diagnosis = '';
    if (indicationSelections.some(i => i.controlName === 'diagnosis')) {
      const indication = indicationSelections.find(i => i.controlName === 'diagnosis');
      div.innerHTML += `<li>${indication.controlDisplayName}: ${indication.selectionDisplayName}</li>`
      diagnosis = indication.selectionValue;
    }

    if (!vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(diagnosis)) {
      if (indicationSelections.some(i => i.controlName === 'criteria')) {
        const indication = indicationSelections.find(i => i.controlName === 'criteria');
        div.innerHTML += `<li style="font-style: italic;">${indication.selectionDisplayName}</li>`
      }

      if (indicationSelections.some(i => i.controlType === ControlTypeEnum.CHECKBOX && i.selectionValue === true)) {
        const hdlCheckbox = indicationSelections.find(i => i.controlName === 'HDL_Checkbox');
        if (hdlCheckbox.selectionValue === true) {
          const hdlMale = indicationSelections.find(i => i.controlName === 'HDL_Male');
          const hdlFemale = indicationSelections.find(i => i.controlName === 'HDL_Female');
          this.createCheckboxMaleFemaleHtml(div, hdlCheckbox, hdlMale, hdlFemale);
        }
        const ldlCheckbox = indicationSelections.find(i => i.controlName === 'LDL_Checkbox');
        if (ldlCheckbox.selectionValue === true) {
          const ldlMale = indicationSelections.find(i => i.controlName === 'LDL_Male');
          const ldlFemale = indicationSelections.find(i => i.controlName === 'LDL_Female');
          this.createCheckboxMaleFemaleHtml(div, ldlCheckbox, ldlMale, ldlFemale);
        }
        const tcCheckbox = indicationSelections.find(i => i.controlName === 'TC_Checkbox');
        if (tcCheckbox.selectionValue === true) {
          const tcMale = indicationSelections.find(i => i.controlName === 'TC_Male');
          const tcFemale = indicationSelections.find(i => i.controlName === 'TC_Female');
          this.createCheckboxMaleFemaleHtml(div, tcCheckbox, tcMale, tcFemale);
        }
        const ttCheckbox = indicationSelections.find(i => i.controlName === 'TT_Checkbox');
        if (ttCheckbox.selectionValue === true) {
          const ttMale = indicationSelections.find(i => i.controlName === 'TT_Male');
          const ttFemale = indicationSelections.find(i => i.controlName === 'TT_Female');
          this.createCheckboxMaleFemaleHtml(div, ttCheckbox, ttMale, ttFemale);
        }
      }
    }

    // append to dom element
    html?.appendChild(div);
  }

  displayHepatitis(indicationSelections: SelectionsModel[]): void {
    // get the dom element to mod
    let html = document.getElementById('hepatitisFilters');

    // clear previous html
    html.innerHTML = '';

    // create a new element to add things to
    let div = document.createElement('div');

    let hasVirusSelections = indicationSelections.some(i => i.controlName === 'virus');

    if (hasVirusSelections) {
      const virus = indicationSelections.find(i => i.controlName === 'virus');
      div.innerHTML += `<li>${virus.controlDisplayName}: ${virus.selectionDisplayName}</li>`

      if (indicationSelections.some(i => i.controlName === 'populations') && ['Hepatitis B', 'Hepatitis C'].includes(virus.selectionValue)) {
        const populationsIndication = indicationSelections.find(i => i.controlName === 'populations');
        div.innerHTML += `<li>${populationsIndication.controlDisplayName}: ${populationsIndication.selectionDisplayName}</li>`

        if (indicationSelections.some(i => i.controlName === 'screeningtest') && virus.selectionValue === 'Hepatitis B' && 
          !vennHealthConstants.diagnosedOrTreatedOnlyCodes.includes(populationsIndication.selectionValue)) {
          const screeningTest = indicationSelections.find(i => i.controlName === 'screeningtest');
          div.innerHTML += `<li>${screeningTest.controlDisplayName}: ${screeningTest.selectionDisplayName}</li>`
        }
      }
    }

    // append to dom element
    html?.appendChild(div);
  }

  displayInsomnia(indicationSelections: SelectionsModel[]): void {
    // get the dom element to mod
    let html = document.getElementById('insomniaFilters');

    // clear previous html
    html.innerHTML = '';

    // create a new element to add things to
    let div = document.createElement('div');

    if (indicationSelections.some(i => i.controlName === 'diagnosis')) {
      const diagnosisIndication = indicationSelections.find(i => i.controlName === 'diagnosis');
      div.innerHTML += `<li>${diagnosisIndication.controlDisplayName}: ${diagnosisIndication.selectionDisplayName}</li>`
    }

    if (indicationSelections.some(i => i.controlName === 'criteria')) {
      const criteriaIndication = indicationSelections.find(i => i.controlName === 'criteria');
      div.innerHTML += `<li style="font-style: italic;">${criteriaIndication.selectionDisplayName}</li>`
    }

    if (indicationSelections.some(i => i.controlType === ControlTypeEnum.DROPDOWN && i.controlName !== 'criteria')) {
      const dropdownFilters = indicationSelections.filter(i => i.controlType === ControlTypeEnum.DROPDOWN && i.controlName !== 'criteria');
      dropdownFilters.forEach(i => {
        this.createDropdownListHtml(div, i.controlDisplayName, i.selectionDisplayName);
      });
    }

    // append to dom element
    html?.appendChild(div);
  }

  getSelectedFilterHeader(): string {
    let header = 'Selected Filters';
    const count = this.allIndications.flatMap(i => i.indications).filter(i => i.selected == true).length ?? 0;
    if (count > 0) {
      header = `${header} (${count})`;
    }
    return header;
  }

  private createCheckboxMaleFemaleHtml(div: HTMLDivElement, checkbox: SelectionsModel, male: SelectionsModel, female: SelectionsModel) {
    div.innerHTML += `<li>${checkbox.controlDisplayName}</li>
        <ul style="list-style-type: none;">
        <li>Male: ${male.selectionValue}</li>
        <li>Female: ${female.selectionValue}</li>
        </ul>`;
  }

  private createDropdownListHtml(div: HTMLDivElement, dropdownLabel: string, selectionDisplayName: string) {
    div.innerHTML += `<li>${dropdownLabel}</li>
        <ul style="list-style-type: none;">
        <li>${selectionDisplayName}</li>
        </ul>`;
  }
}
